import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useSEO } from "@/lib/useSEO";
import { TESTIMONIALS } from "@/lib/marketingData";
import {
  Sparkles, Clock, BarChart3, Award, BookOpen, Brain, TrendingUp,
  Zap, MapPin, Shield, ChevronRight, PlayCircle
} from "lucide-react";

const EXAM_CARDS = [
  { slug: "jee", name: "JEE Main & Advanced", desc: "Real NTA-style CBT for Physics, Chemistry, Maths.", accent: "bg-primary/10 text-primary" },
  { slug: "neet", name: "NEET UG", desc: "NCERT-tagged MCQs, chapter-wise & full mocks.", accent: "bg-accent/15 text-accent" },
  { slug: "olympiads", name: "Olympiads & NTSE", desc: "NSO, IMO, NSTSE, NTSE — from Class 6 onwards.", accent: "bg-secondary/15 text-secondary" },
  { slug: "govt-exams", name: "SSC · Banking · Govt", desc: "CGL, CHSL, IBPS, SBI, RRB — one platform.", accent: "bg-emerald-500/10 text-emerald-600" },
];

const FEATURES = [
  { icon: Clock, title: "Real CBT interface", body: "Identical timer, palette, section switch, and mark-for-review as the actual JEE / NEET paper." },
  { icon: BarChart3, title: "Deep analytics", body: "Chapter-wise accuracy, speed-vs-accuracy, All India rank estimate — after every attempt." },
  { icon: Brain, title: "AI recommendations", body: "Weak-topic drills, personalised study plans, next-best-test suggestions — powered by your own attempts." },
  { icon: BookOpen, title: "5 lakh+ question bank", body: "NCERT-tagged MCQs, PYQs, integer, multi-correct, assertion-reason — every type you'll ever face." },
  { icon: Zap, title: "Daily practice + streaks", body: "Baby tests, DPP-style drills, and streak tracking — the discipline that quietly builds a rank." },
  { icon: Shield, title: "Reattempt any test", body: "Retake mocks, watch solutions, revisit wrong questions — no cap, ever." },
];

export default function MarketingHome() {
  useSEO({
    title: "Mock Test Club — AI-Powered CBT Mock Tests for JEE, NEET & Competitive Exams",
    description: "Free & premium online test series for JEE Main, JEE Advanced, NEET UG, Olympiads and govt exams. Real CBT interface, 5+ lakh questions, AI analytics, rank prediction. Start free.",
    canonical: "https://exam-builder-hub.emergent.host/",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Mock Test Club",
        "url": "https://exam-builder-hub.emergent.host/",
        "logo": "https://exam-builder-hub.emergent.host/manifest.json",
        "sameAs": [],
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "Mock Test Club",
        "url": "https://exam-builder-hub.emergent.host/",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://exam-builder-hub.emergent.host/blog?q={search_term_string}",
          "query-input": "required name=search_term_string",
        },
      },
    ],
  });

  return (
    <div data-testid="marketing-home">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 en-grid-bg opacity-30" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 en-fade-up">
            <Badge className="rounded-full mb-6 bg-secondary/15 text-secondary hover:bg-secondary/20 border border-secondary/30">
              <Sparkles className="h-3.5 w-3.5 mr-1.5" /> AI-powered CBT · JEE · NEET · Olympiads
            </Badge>
            <h1 className="font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
              AI-Powered CBT Mock Tests<br/>
              for <span className="text-primary">JEE, NEET</span> &<br/>
              Competitive Exams
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-xl leading-relaxed">
              Real NTA-style exam interface. 5 lakh+ NCERT-tagged questions. Chapter-wise, PYQs, full mocks. AI weak-topic drills and All India rank prediction after every attempt.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/signup"><Button data-testid="hero-start-free" size="lg" className="rounded-full px-8">Start free mock <ChevronRight className="h-4 w-4 ml-1" /></Button></Link>
              <Link to="/jee"><Button data-testid="hero-explore-jee" size="lg" variant="outline" className="rounded-full px-8">Explore JEE series</Button></Link>
              <Link to="/neet"><Button data-testid="hero-explore-neet" size="lg" variant="ghost" className="rounded-full px-6"><PlayCircle className="h-4 w-4 mr-1.5" />Explore NEET series</Button></Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 text-sm text-muted-foreground">
              <Stat value="5 L+" label="Curated questions" />
              <Stat value="1,000+" label="CBT mocks" />
              <Stat value="12+" label="Cities served" />
              <Stat value="4.7★" label="Aspirant rating" />
            </div>
          </div>
          <div className="lg:col-span-5 en-fade-up" style={{animationDelay: "150ms"}}>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1622151834677-70f982c9adef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwzfHxzdHVkZW50JTIwc3R1ZHlpbmclMjBsYXB0b3B8ZW58MHx8fHwxNzg1NDkzMzYwfDA&ixlib=rb-4.1.0&q=85"
                alt="Aspirant taking a CBT mock test on Mock Test Club"
                className="rounded-3xl border border-border shadow-2xl object-cover w-full aspect-[4/5]"
                loading="lazy"
              />
              <Card className="absolute -bottom-4 -left-4 p-4 w-64 shadow-xl border-border rounded-2xl bg-card">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-accent/15 text-accent grid place-items-center"><TrendingUp className="h-5 w-5" /></div>
                  <div>
                    <div className="text-xs text-muted-foreground">After 4 mocks</div>
                    <div className="font-semibold text-sm">AIR moved 12,400 → 3,180</div>
                  </div>
                </div>
              </Card>
              <Card className="absolute -top-4 -right-4 p-3 shadow-xl border-border rounded-2xl bg-card">
                <div className="flex items-center gap-2">
                  <Award className="h-4 w-4 text-accent" />
                  <div className="text-xs font-semibold">Rank predictor live</div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* EXAM CATEGORIES */}
      <section className="max-w-7xl mx-auto px-6 py-16 lg:py-20">
        <div className="max-w-2xl mb-10">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Pick your exam</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight mt-2">Every serious exam. One CBT platform.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {EXAM_CARDS.map((e) => (
            <Link key={e.slug} to={`/${e.slug}`} data-testid={`exam-card-${e.slug}`}>
              <Card className="en-card p-6 h-full flex flex-col">
                <div className={`h-11 w-11 rounded-xl grid place-items-center mb-4 ${e.accent}`}>
                  <BookOpen className="h-5 w-5" />
                </div>
                <h3 className="font-display font-semibold text-lg">{e.name}</h3>
                <p className="text-sm text-muted-foreground mt-2 flex-1">{e.desc}</p>
                <div className="mt-4 text-sm text-primary font-medium inline-flex items-center gap-1">
                  Explore series <ChevronRight className="h-3.5 w-3.5" />
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="bg-card/40 border-y border-border">
        <div className="max-w-7xl mx-auto px-6 py-16 lg:py-20">
          <div className="max-w-2xl mb-10">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Why Mock Test Club</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight mt-2">CBT + AI + NCERT-line discipline.</h2>
            <p className="text-muted-foreground mt-3">Everything a coaching institute gives you — plus AI that watches your weak topics and rebuilds them for you.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <Card key={f.title} className="en-card p-6 en-fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`home-feature-${i}`}>
                <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary grid place-items-center mb-4">
                  <f.icon className="h-5 w-5" />
                </div>
                <h3 className="font-display font-semibold text-lg mb-2">{f.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.body}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CBT PREVIEW */}
      <section className="max-w-7xl mx-auto px-6 py-16 lg:py-20 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-primary">The CBT engine</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight mt-2">Feels exactly like exam day. Because we designed it that way.</h2>
          <p className="text-muted-foreground mt-4 leading-relaxed">
            Same palette colours, same mark-for-review behaviour, same section switch, same auto-submit. Nothing surprises you at the NEET / JEE centre.
          </p>
          <ul className="mt-6 space-y-3 text-sm">
            {["NTA-identical palette & colour codes", "Sticky timer with auto-submit", "Question palette · answered / marked / mixed", "Section navigation with unsaved-warning", "Mark for review · save & next · clear response"].map(x => (
              <li key={x} className="flex items-start gap-2"><span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />{x}</li>
            ))}
          </ul>
          <div className="mt-6 flex gap-3">
            <Link to="/signup"><Button data-testid="cbt-start-btn" className="rounded-full">Try the CBT engine free</Button></Link>
            <Link to="/how-it-works"><Button variant="outline" className="rounded-full">How it works</Button></Link>
          </div>
        </div>
        <div className="lg:col-span-7">
          <div className="relative rounded-3xl border border-border bg-card p-4 shadow-xl">
            <div className="h-10 flex items-center gap-2 px-2 border-b border-border">
              <div className="h-2.5 w-2.5 rounded-full bg-destructive/60" />
              <div className="h-2.5 w-2.5 rounded-full bg-amber-500/70" />
              <div className="h-2.5 w-2.5 rounded-full bg-emerald-500/70" />
              <div className="text-xs text-muted-foreground ml-2 font-mono">exam-builder-hub · Live Exam</div>
              <div className="ml-auto text-xs font-mono font-bold text-primary">02:14:37</div>
            </div>
            <div className="grid grid-cols-12 gap-4 mt-4">
              <div className="col-span-8 space-y-3">
                <div className="flex gap-2 text-xs"><Badge variant="secondary" className="rounded-full">Q3/60</Badge><Badge variant="outline" className="rounded-full">Physics</Badge><Badge variant="outline" className="rounded-full">medium</Badge></div>
                <div className="text-sm leading-relaxed">A projectile is fired at 45° with initial speed 20 m/s. What is the maximum height reached? (g=10 m/s²)</div>
                <div className="space-y-2">
                  {["A · 5 m", "B · 10 m", "C · 20 m", "D · 40 m"].map((opt, i) => (
                    <div key={i} className={`text-sm p-2.5 rounded-lg border ${i===1 ? "border-primary bg-primary/10" : "border-border"}`}>{opt}</div>
                  ))}
                </div>
              </div>
              <div className="col-span-4 space-y-2">
                <div className="text-xs font-semibold">Palette</div>
                <div className="grid grid-cols-5 gap-1.5">
                  {Array.from({length: 20}).map((_, i) => (
                    <div key={i} className={`h-7 rounded text-[10px] grid place-items-center font-bold ${
                      i < 3 ? "bg-emerald-500 text-white" :
                      i === 3 ? "bg-primary text-primary-foreground ring-2 ring-primary" :
                      i === 4 ? "bg-accent text-accent-foreground" :
                      "bg-muted"
                    }`}>{i+1}</div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-card/40 border-y border-border">
        <div className="max-w-7xl mx-auto px-6 py-16 lg:py-20">
          <div className="max-w-2xl mb-10">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Real ranks. Real students.</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight mt-2">The scoreboard speaks for itself.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {TESTIMONIALS.map((t) => (
              <Card key={t.name} className="en-card p-5" data-testid={`testimonial-${t.name.split(" ")[0].toLowerCase()}`}>
                <div className="text-2xl leading-none text-primary">“</div>
                <p className="text-sm mt-2 leading-relaxed">{t.quote}</p>
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="font-semibold text-sm">{t.name}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><MapPin className="h-3 w-3" /> {t.role} · {t.city}</div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CITIES */}
      <section className="max-w-7xl mx-auto px-6 py-16 lg:py-20">
        <div className="max-w-2xl mb-10">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Study from anywhere</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight mt-2">Kota-level test discipline. From your bedroom.</h2>
          <p className="text-muted-foreground mt-3">Whether you're in Delhi, Kota, Patna, Lucknow or Bengaluru — Mock Test Club delivers the same test rigor top coaching centres charge lakhs for.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {["Delhi NCR", "Kota", "Patna", "Lucknow", "Jaipur", "Hyderabad", "Mumbai", "Kolkata", "Chennai", "Bengaluru", "Ahmedabad", "Pune"].map((c) => (
            <Link key={c} to={`/cities/${c.toLowerCase().replace(/\s+/g, "-").replace(/\snc.*/, "").replace("delhi", "delhi")}`}
              className="rounded-full border border-border px-4 py-2 text-sm hover:border-primary hover:text-primary transition-colors duration-200">
              {c}
            </Link>
          ))}
          <Link to="/cities" className="rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-medium">See all cities →</Link>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-20">
        <Card className="bg-primary text-primary-foreground p-10 lg:p-14 rounded-3xl border-0 relative overflow-hidden">
          <div className="absolute inset-0 en-grid-bg opacity-10" aria-hidden="true" />
          <div className="relative grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight leading-tight">Your rank starts with a single mock.</h2>
              <p className="mt-4 text-primary-foreground/80 max-w-lg">Sign up in 30 seconds. Take your first CBT mock in 60. See your rank in 90.</p>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end">
              <Link to="/signup"><Button data-testid="footer-cta-signup" size="lg" variant="secondary" className="rounded-full px-8">Start free mock</Button></Link>
              <Link to="/pricing"><Button data-testid="footer-cta-pricing" size="lg" variant="outline" className="rounded-full px-8 border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground">See plans</Button></Link>
            </div>
          </div>
        </Card>
      </section>
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <div className="font-display font-bold text-2xl text-foreground">{value}</div>
      <div className="text-xs">{label}</div>
    </div>
  );
}
